import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export type LogLevel = 'info' | 'warn' | 'error';
export type LogSource = 'ui' | 'edge' | 'db' | 'cron';

export interface LogEventParams {
  level: LogLevel;
  source?: LogSource;
  eventKey: string;
  message: string;
  domain?: string;
  entityType?: string;
  entityId?: string;
  producerId?: string;
  context?: Record<string, unknown>;
  requestId?: string;
}

/**
 * Log a system event to the database.
 * 
 * IMPORTANT: Do not log sensitive data like tokens, passwords, or PII.
 * Mask or omit any sensitive fields before logging.
 * 
 * @example
 * await logSystemEvent({
 *   level: 'error',
 *   eventKey: 'checkout.payment_failed',
 *   message: 'Payment failed for checkout',
 *   domain: 'marketplace',
 *   entityType: 'checkout',
 *   entityId: checkoutId,
 *   context: { errorCode: 'card_declined' },
 * });
 */
export async function logSystemEvent(params: LogEventParams): Promise<string | null> {
  try {
    // Mask any potentially sensitive fields in context
    const safeContext = maskSensitiveData(params.context || {}) as Json;

    const { data, error } = await supabase.rpc('log_system_event', {
      p_level: params.level,
      p_source: params.source || 'ui',
      p_event_key: params.eventKey,
      p_message: params.message,
      p_domain: params.domain || null,
      p_entity_type: params.entityType || null,
      p_entity_id: params.entityId || null,
      p_producer_id: params.producerId || null,
      p_context: safeContext,
      p_request_id: params.requestId || null,
    });

    if (error) {
      console.error('[logSystemEvent] Failed to log event:', error);
      return null;
    }

    return data as string;
  } catch (err) {
    console.error('[logSystemEvent] Exception:', err);
    return null;
  }
}

/**
 * Mask sensitive fields in context object.
 * This is a basic implementation - extend as needed.
 */
function maskSensitiveData(context: Record<string, unknown>): Record<string, unknown> {
  const sensitiveKeys = [
    'password', 'token', 'secret', 'apiKey', 'api_key',
    'authorization', 'auth', 'credential', 'private',
    'ssn', 'socialSecurityNumber', 'creditCard', 'cardNumber',
  ];

  const masked: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(context)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      masked[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      masked[key] = maskSensitiveData(value as Record<string, unknown>);
    } else {
      masked[key] = value;
    }
  }

  return masked;
}

/**
 * Convenience function for logging errors with stack trace
 */
export async function logError(
  eventKey: string,
  error: Error | unknown,
  additionalContext?: Record<string, unknown>
): Promise<string | null> {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  return logSystemEvent({
    level: 'error',
    eventKey,
    message: errorMessage,
    context: {
      ...additionalContext,
      stack: errorStack,
    },
  });
}

/**
 * Convenience function for logging warnings
 */
export async function logWarning(
  eventKey: string,
  message: string,
  context?: Record<string, unknown>
): Promise<string | null> {
  return logSystemEvent({
    level: 'warn',
    eventKey,
    message,
    context,
  });
}

/**
 * Convenience function for logging info
 */
export async function logInfo(
  eventKey: string,
  message: string,
  context?: Record<string, unknown>
): Promise<string | null> {
  return logSystemEvent({
    level: 'info',
    eventKey,
    message,
    context,
  });
}
